<template lang="pug">
  .b-select(@click="toggleDropdown" :class="{'page-size': pageSize}")
    ._label(v-if="labelOuter") {{ label }}
    ._inner.-with-arrow.box(:class="{ error: !!error }")
      Icon._icon(v-if="icon" :name="icon")
      span._selected-value(v-if="selectedItem" :class="selectedIcon") {{ selectedItemText }}
        UserTile(v-if="selectedItem.user" :user="selectedItem.user" :size="30")
      span._placeholder(v-else) {{ labelOuter ? notSelectedLabel : label }}
      slot
    ._dropdown(:class="{ opened }" :style="{ top: verticalShift }")
      ._list(ref="list")
        ._item(v-if="showNotSelectedItem" @click="selectOption(notSelectedValue)") {{ notSelectedLabel }}
        ._item(
          v-for="item in optionsCompiled"
          :key="item.value"
          @click="selectOption(item.value, item.icon)"
          :class="[{ selected: item.value === value}, item.icon]"
        ) {{ item.label }}
          UserTile(v-if="item.user" :user="item.user" :size="30")
    ._error-text(v-if="error") {{ error }}
</template>

<script>
import UserTile from '@/components/UserTile.vue';

export default {
  name: 'BaseSelect',
  components: { UserTile },
  props: {
    options: {
      type: [Object, Array],
      required: true,
    },
    value: {
      type: [String, Number, Boolean],
      default: null,
    },
    notSelectedLabel: {
      type: String,
      default: 'Не выбрано',
    },
    notSelectedValue: {
      type: String,
      default: null,
    },
    hideNotSelected: {
      type: Boolean,
      default: false,
    },
    label: String,
    labelOuter: Boolean,
    icon: String,
    pageSize: Boolean,
    error: String,
  },
  data() {
    return {
      opened: false,
      verticalShift: '0',
      selectedIcon: null,
    };
  },
  watch: {
    opened(val) {
      if (!val) {
        this.verticalShift = '0';
        return;
      }

      this.$nextTick(() => {
        const bottomOfWindow = window.innerHeight;
        const bottomOfDropdown = this.$refs.list.getBoundingClientRect().bottom;

        const shift = bottomOfWindow - bottomOfDropdown;
        this.verticalShift = shift < 0 ? `${shift}px` : '0';
      });
    },
  },
  computed: {
    selectedItem() { return this.optionsCompiled.find((option) => option.value === this.value); },
    selectedItemText() { return this.selectedItem?.label ?? ''; },
    showNotSelectedItem() {
      return !this.hideNotSelected && this.value !== this.notSelectedValue;
    },
    optionsCompiled() {
      if (!Array.isArray(this.options)) {
        return Object.entries(this.options).map(([value, label]) => ({ value, label }));
      }

      if (typeof this.options?.[0] !== 'object') {
        return this.options.map(v => ({ value: v, label: v }));
      }

      return this.options;
    },
  },
  methods: {
    selectOption(value, icon) {
      this.$emit('input', value);
      this.$emit('change', value);
      this.selectedIcon = icon;
    },
    toggleDropdown() {
      this.opened = !this.opened;
    },
    close(e) {
      if (!this.$el.contains(e.target)) {
        this.opened = false;
      }
    },
  },
  // Слушаем в capture-фазе: BasePopup глушит всплытие клика на своём ._spacer,
  // поэтому внутри попапа список иначе не закрывался бы по клику мимо
  mounted() {
    document.addEventListener('click', this.close, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.close, true);
  },
};
</script>

<style scoped lang="scss">

.select {
  cursor: pointer;

  &.page-size {
    display: flex;
    align-items: center;

    .select__label {
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      color: var(--color-light-dark);
      margin-bottom: 0;
      margin-right: 8px;
    }

    .select__inner {
      height: 20px;
      width: 53px;
      background-color: #FFFFFF;
      border: 1px solid #EBEFF2;
      border-radius: 4px;
      padding-left: 6px;
      color: var(--color-light-dark);
      font-weight: 400;
    }

    .select__inner--with-arrow {
      background-position: top 6px right 4px;
    }
  }

  &__label {
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    letter-spacing: 0.01em;
    color: #92A0B0;
    margin-bottom: 8px;
  }

  &__inner {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    border: none;
    padding: 0 40px 0 16px;
    height: 38px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-dark-gray);

    &--with-arrow {
      background-image: url(../assets/img/svg/angle-down.svg);
      background-color: var(--color-super-light);
      background-size: 12px;
      background-position: top 16px right 15px;
      background-repeat: no-repeat;
    }

    &.error {
      border: 1px solid var(--color-red);
      margin-bottom: 4px;
    }
  }

  &__error-text {
    max-width: 286px;
    text-align: left;
    color: var(--color-red);
    letter-spacing: .01em;
    font-weight: 500;
    line-height: 13px;
    font-size: 10px;
    padding: 0 16px;
  }

  &__placeholder {
    color: var(--color-middle-gray);
  }

  &__icon {
    margin-right: 11px;
  }
  &__dropdown {
    position: relative;
  }

  &__dropdown.opened &__list {
    visibility: visible;
  }

  &__list {
    box-sizing: border-box;
    padding: 8px 0;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.08);
    margin-top: 4px;
    border: none;
    border-radius: 6px;
    position: absolute;

    max-height: 350px;
    overflow: auto;
    will-change: visibility;

    visibility: hidden;
    z-index: 1010;
    background-color: #ffffff;
    top: 100%;
  }

  &__item {
    padding: 12px 0;
    margin: 0 20px;
    cursor: pointer;
    color: var(--color-middle-gray);
    font-size: 14px;
    font-weight: 500;
    border-top: 1px solid var(--color-purple);
    &:first-child {
      border-top-color: transparent;
    }

    &:hover, &.selected {
      padding: 12px 20px;
      margin: 0;
      border-top-color: transparent;
    }

    &:hover + &, &.selected + & {
      border-top-color: transparent;
    }

    &.selected {
      background: #ECF1FF;
    }

    &:hover {
      background: #E1E7F8;
    }
  }
  &__item {
    &--selectable {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
  }

  .select__item {
    &.arrow {
      &-up:after {
        content: "→";
        transform: rotate(-90deg);
        float: right;
        border: none;
        font-size: 18px;
        position: relative;
        bottom: 3px;
        left: 2px;
      }

      &-down:after {
        content: "→";
        transform: rotate(90deg);
        float: right;
        border: none;
        font-size: 20px;
        position: relative;
        bottom: 2px;
        left: 5px;
      }
    }
  }

  .select__selected-value {
    &.arrow {
      &-up, &-down {
        display: flex;
        align-items: center;
      }
      &-up:after {
        content: "→";
        transform: rotate(-90deg);
        float: right;
        border: none;
        font-size: 18px;
        position: relative;
        left: 2px;
      }

      &-down:after {
        content: "→";
        transform: rotate(90deg);
        float: right;
        border: none;
        font-size: 20px;
        position: relative;
        left: 5px;
      }
    }
  }
}

</style>
